// =============================================================================
// Library Batch Review — poll progress, retry FAILED, discard, finish
// =============================================================================
// Card-list layout matching the upload queue: thumbnail preview + truncated
// (expandable) SKU/file + category/tag chips + per-row actions. Polls every
// 2.5s while PROCESSING. "Retry" (reprocess) is FAILED-only; "Discard"
// archives; "Finish review" settles the batch (confirms on remaining FAILED).
// Multi-select drives a bulk category/tag assign bar.
// =============================================================================

import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Badge, Button, Card, CardHeader, Combobox, Input, Spinner, useConfirm } from '@tkpod/ui';
import type { ComboboxOption } from '@tkpod/ui';
import { ApiClientError } from '@tkpod/api-client';
import { toast } from '@/lib/toast';
import {
  archiveAsset,
  createTag,
  fetchBatch,
  fetchCategories,
  fetchTags,
  finishBatch,
  flattenCategories,
  libraryKeys,
  patchAsset,
  reprocessAsset,
} from './api';
import { LibraryBulkBar } from './LibraryBulkBar';

/** Sort weight: rows needing action first (plan: action-focused review). */
const STATUS_WEIGHT: Record<string, number> = { FAILED: 0, PROCESSING: 1 };

function assetVariant(s: string): 'success' | 'warning' | 'danger' | 'default' {
  if (s === 'READY') return 'success';
  if (s === 'PROCESSING') return 'warning';
  if (s === 'FAILED') return 'danger';
  return 'default';
}

export function LibraryBatchReviewPage() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { confirm, dialog } = useConfirm();

  const batchQuery = useQuery({
    queryKey: libraryKeys.batch(id),
    queryFn: () => fetchBatch(id),
    enabled: !!id,
    // Poll while the batch is PROCESSING *or* any asset row is — a retry
    // re-queues an asset without changing the batch status (stays REVIEW),
    // and the row would otherwise sit on PROCESSING until a manual reload.
    refetchInterval: (query) => {
      const data = query.state.data;
      if (!data) return false;
      const active =
        data.batch.status === 'PROCESSING' ||
        data.assets.some((a) => a.status === 'PROCESSING');
      return active ? 2500 : false;
    },
  });
  const categoriesQuery = useQuery({ queryKey: libraryKeys.categories, queryFn: fetchCategories });
  const tagsQuery = useQuery({ queryKey: libraryKeys.tags, queryFn: fetchTags });

  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editSku, setEditSku] = useState('');
  // Quick status filter over the (fully loaded) batch asset list.
  const [statusFilter, setStatusFilter] = useState('');

  const categoryOptions = useMemo(
    () => flattenCategories(categoriesQuery.data ?? []),
    [categoriesQuery.data],
  );
  const tagOptions = useMemo(
    () => (tagsQuery.data ?? []).map((t) => ({ value: t.id, label: t.name })),
    [tagsQuery.data],
  );

  /** Inline tag creation (row "+ tag" pickers) — invalidates the tag list. */
  const onCreateTag = async (name: string): Promise<ComboboxOption> => {
    try {
      const created = await createTag({ name });
      void queryClient.invalidateQueries({ queryKey: libraryKeys.tags });
      return { value: created.id, label: created.name };
    } catch (e) {
      toast((e as Error).message, 'error');
      throw e;
    }
  };

  const invalidate = () => queryClient.invalidateQueries({ queryKey: libraryKeys.batch(id) });

  const toggleSelect = (assetId: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(assetId)) next.delete(assetId);
      else next.add(assetId);
      return next;
    });

  const skuMutation = useMutation({
    mutationFn: (vars: { assetId: string; sku: string }) => patchAsset(vars.assetId, { sku: vars.sku }),
    onSuccess: () => { toast('SKU updated', 'success'); setEditingId(null); void invalidate(); },
    onError: (e: Error) => toast(e.message, 'error'),
  });
  // Per-row inline category/tag edit (plan §7.4 cell-level inline edit).
  const rowPatchMutation = useMutation({
    mutationFn: (v: { assetId: string; body: Record<string, unknown> }) => patchAsset(v.assetId, v.body),
    onSuccess: () => { void invalidate(); },
    onError: (e: Error) => toast(e.message, 'error'),
  });
  const rowPatch = (assetId: string, body: Record<string, unknown>) =>
    rowPatchMutation.mutate({ assetId, body });
  // Scope the pending state to the row being patched — a single shared
  // isPending would freeze every row's controls during any one edit.
  const rowPatchPending = (assetId: string) =>
    rowPatchMutation.isPending && rowPatchMutation.variables?.assetId === assetId;
  const retryMutation = useMutation({
    mutationFn: (assetId: string) => reprocessAsset(assetId),
    onSuccess: () => { toast('Retry queued', 'success'); void invalidate(); },
    onError: (e: Error) => toast(e.message, 'error'),
  });
  // Variant links: most suffix variants are auto-linked at init (shown as a
  // chip with one-click Unlink); the remaining suggestions are linked on
  // explicit approval — per row or all at once.
  const linkVariantMutation = useMutation({
    mutationFn: (v: { assetId: string; primaryAssetId: string; label: string }) =>
      patchAsset(v.assetId, { primaryAssetId: v.primaryAssetId, variantLabel: v.label }),
    onSuccess: () => { toast('Variant linked', 'success'); void invalidate(); },
    onError: (e: Error) => toast(e.message, 'error'),
  });
  const unlinkVariantMutation = useMutation({
    mutationFn: (assetId: string) => patchAsset(assetId, { primaryAssetId: null }),
    onSuccess: () => { toast('Variant unlinked', 'success'); void invalidate(); },
    onError: (e: Error) => toast(e.message, 'error'),
  });
  const [linkingAll, setLinkingAll] = useState(false);
  const linkAllSuggested = async (rows: { id: string; variantSuggestion: { primaryAssetId: string; primarySku: string; label: string } | null }[]) => {
    const targets = rows.filter((r) => r.variantSuggestion);
    if (!targets.length) return;
    setLinkingAll(true);
    let okCount = 0;
    let failCount = 0;
    for (const r of targets) {
      try {
        await patchAsset(r.id, {
          primaryAssetId: r.variantSuggestion!.primaryAssetId,
          variantLabel: r.variantSuggestion!.label,
        });
        okCount += 1;
      } catch {
        failCount += 1;
      }
    }
    toast(
      `Linked ${okCount} variant(s)${failCount ? ` · ${failCount} failed` : ''}`,
      failCount ? 'warning' : 'success',
    );
    setLinkingAll(false);
    void invalidate();
  };
  const [discardingId, setDiscardingId] = useState<string | null>(null);
  /**
   * Discard flow: confirm → archive WITHOUT bypass flags. If the server
   * rejects with a 409 guard (asset has variants / is used by designs), show
   * the server's message in a second confirm and only then retry with the
   * bypass flags. `force` is never sent blindly.
   */
  const onDiscard = async (asset: { id: string; sku: string; status: string }) => {
    const isFailed = asset.status === 'FAILED';
    const ok = await confirm({
      title: isFailed ? 'Delete asset' : 'Discard asset',
      description: isFailed
        ? `"${asset.sku}" failed to process and cannot be used. It will be permanently deleted.`
        : `Discard "${asset.sku}"? It will be archived and removed from this batch's counts.`,
      confirmLabel: isFailed ? 'Delete' : 'Discard',
      tone: 'danger',
    });
    if (!ok) return;

    setDiscardingId(asset.id);
    try {
      let res: { archived: number; hardDeleted?: boolean };
      try {
        res = await archiveAsset(asset.id, {});
      } catch (e) {
        if (!(e instanceof ApiClientError) || e.statusCode !== 409) throw e;
        // Guard hit (variants / usage) — surface the server's reason and ask
        // again before overriding.
        const proceed = await confirm({
          title: 'Discard anyway?',
          description: `${e.message} — discard anyway?`,
          confirmLabel: 'Discard anyway',
          tone: 'danger',
        });
        if (!proceed) return;
        res = await archiveAsset(asset.id, { withVariants: true, force: true });
      }
      toast(res.hardDeleted ? 'Deleted permanently' : 'Discarded', 'success');
      void invalidate();
    } catch (e) {
      toast((e as Error).message, 'error');
    } finally {
      setDiscardingId(null);
    }
  };
  const finishMutation = useMutation({
    mutationFn: () => finishBatch(id),
    onSuccess: (res) => {
      toast(`Batch completed${res.failedRemaining ? ` (${res.failedRemaining} failed remain)` : ''}`, 'success');
      // The batch just went read-only — a stale selection would leave the
      // bulk bar floating over a view with no checkboxes.
      setSelected(new Set());
      void queryClient.invalidateQueries({ queryKey: ['library'] });
    },
    onError: (e: Error) => toast(e.message, 'error'),
  });

  const batch = batchQuery.data?.batch;
  const assets = batchQuery.data?.assets ?? [];
  const failedCount = assets.filter((a) => a.status === 'FAILED').length;
  const processingCount = assets.filter((a) => a.status === 'PROCESSING').length;
  // COMPLETED batches are a historical record: every edit affordance is
  // hidden and asset management moves to the Assets page.
  const readOnly = batch?.status === 'COMPLETED';
  // Action-focused ordering: FAILED rows (need retry/delete decisions) first,
  // then PROCESSING, then the rest in upload order.
  const visibleAssets = useMemo(() => {
    const filtered = statusFilter
      ? assets.filter((a) => a.status === statusFilter)
      : assets;
    return [...filtered].sort(
      (a, b) => (STATUS_WEIGHT[a.status] ?? 2) - (STATUS_WEIGHT[b.status] ?? 2),
    );
  }, [assets, statusFilter]);
  const statusCounts = useMemo(() => {
    const counts = new Map<string, number>();
    for (const a of assets) counts.set(a.status, (counts.get(a.status) ?? 0) + 1);
    return counts;
  }, [assets]);
  const hasFailedSelected = assets.some(
    (a) => selected.has(a.id) && a.status === 'FAILED',
  );
  const allSelected =
    visibleAssets.length > 0 && visibleAssets.every((a) => selected.has(a.id));
  const suggestionCount = readOnly
    ? 0
    : assets.filter((a) => a.variantSuggestion).length;

  const onFinish = async () => {
    if (failedCount > 0) {
      const ok = await confirm({
        title: 'Finish review',
        description: `${failedCount} failed asset(s) will remain in the library. Continue?`,
        confirmLabel: 'Finish',
      });
      if (!ok) return;
    }
    finishMutation.mutate();
  };

  if (batchQuery.isLoading) {
    return <div className="flex justify-center py-24"><Spinner size="lg" label="Loading batch…" /></div>;
  }
  if (batchQuery.isError || !batch) {
    return <Card><p className="py-8 text-center text-sm text-red-400">Batch not found.</p></Card>;
  }

  const settled = batch.readyCount + batch.failedCount;
  const pct = batch.totalCount ? Math.round((settled / batch.totalCount) * 100) : 0;

  return (
    <div className="space-y-6 pb-24">
      {dialog}
      <Button variant="ghost" size="sm" onClick={() => navigate('/library/uploads')}>← Back to batches</Button>

      <Card>
        <CardHeader
          title={batch.name}
          description={[
            batch.status,
            `${settled}/${batch.totalCount} processed`,
            `${batch.failedCount} failed`,
            batch.createdByName ? `uploaded by ${batch.createdByName}` : null,
          ].filter(Boolean).join(' · ')}
          action={
            batch.status === 'REVIEW' ? (
              <Button
                size="sm"
                loading={finishMutation.isPending}
                // Finishing while workers still own rows would freeze the
                // batch record with counts that change afterwards; the API
                // rejects it with 409 — disable up front instead.
                disabled={processingCount > 0}
                title={processingCount > 0 ? `${processingCount} asset(s) still processing` : undefined}
                onClick={() => void onFinish()}
              >
                {processingCount > 0 ? `Processing ${processingCount}…` : 'Finish review'}
              </Button>
            ) : undefined
          }
        />
        <div className="mt-3 h-2 overflow-hidden rounded bg-surface-tertiary">
          <div className="h-full bg-brand-500" style={{ width: `${pct}%` }} />
        </div>
      </Card>

      {readOnly && (
        <Card>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="text-sm text-text-secondary">
              This batch is completed and read-only. Manage these assets from the Assets page.
            </p>
            <Button size="sm" variant="secondary" onClick={() => navigate('/library')}>
              Go to Assets
            </Button>
          </div>
        </Card>
      )}

      <Card>
        {/* Select-all header + colour legend */}
        <div className="flex items-center gap-3 border-b border-brand-900 pb-3">
          {!readOnly && (
            <input
              type="checkbox"
              checked={allSelected}
              onChange={() => setSelected(allSelected ? new Set() : new Set(visibleAssets.map((a) => a.id)))}
            />
          )}
          <span className="text-sm text-text-secondary">
            {selected.size > 0
              ? `${selected.size} selected`
              : `${visibleAssets.length}${statusFilter ? ` of ${assets.length}` : ''} asset(s)`}
          </span>
          {/* Status count chips — click to filter (action-focused review) */}
          <div className="flex items-center gap-1.5">
            <button
              type="button"
              onClick={() => setStatusFilter('')}
              className={`rounded-full px-2.5 py-0.5 text-xs font-medium transition-colors ${
                statusFilter === ''
                  ? 'bg-brand-500 text-white'
                  : 'bg-surface-tertiary text-text-secondary hover:text-text-primary'
              }`}
            >
              All {assets.length}
            </button>
            {(['FAILED', 'PROCESSING', 'READY', 'ARCHIVED'] as const)
              .filter((s) => statusCounts.has(s))
              .map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => setStatusFilter(statusFilter === s ? '' : s)}
                  className={`rounded-full px-2.5 py-0.5 text-xs font-medium transition-colors ${
                    statusFilter === s
                      ? 'bg-brand-500 text-white'
                      : s === 'FAILED'
                        ? 'bg-red-500/10 text-red-500 hover:bg-red-500/20'
                        : s === 'PROCESSING'
                          ? 'bg-amber-500/10 text-amber-600 hover:bg-amber-500/20'
                          : 'bg-surface-tertiary text-text-secondary hover:text-text-primary'
                  }`}
                >
                  {s.toLowerCase()} {statusCounts.get(s)}
                </button>
              ))}
          </div>
          {suggestionCount > 0 && (
            <Button
              size="sm"
              variant="outline"
              loading={linkingAll}
              onClick={() => void linkAllSuggested(assets)}
              title="Link every suggested variant to its primary"
            >
              Link {suggestionCount} suggested variant{suggestionCount === 1 ? '' : 's'}
            </Button>
          )}
          <div className="ml-auto flex items-center gap-2 text-xs text-text-tertiary">
            <span>Legend:</span>
            <Badge variant="info" size="sm">Category</Badge>
            <span className="inline-flex items-center rounded-full bg-orange-500/15 px-2 py-0.5 text-xs font-medium text-orange-600">Tag</span>
          </div>
        </div>

        <div className="divide-y divide-brand-900">
          {visibleAssets.map((a) => {
            return (
              <div key={a.id} className="flex items-start gap-3 py-3">
                {!readOnly && (
                  <input
                    type="checkbox"
                    checked={selected.has(a.id)}
                    onChange={() => toggleSelect(a.id)}
                    className="mt-3"
                  />
                )}
                {/* Thumbnail */}
                <button
                  type="button"
                  onClick={() => navigate(`/library/assets/${a.id}`)}
                  className="mt-0.5 flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-border bg-[#bdbdbd]"
                >
                  {a.thumbUrl ? (
                    <img src={a.thumbUrl} alt={a.sku} className="max-h-full max-w-full object-contain" />
                  ) : (
                    <div className="flex h-full w-full items-center justify-center text-text-tertiary">
                      <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" d="m2.25 15.75 5.159-5.159a2.25 2.25 0 0 1 3.182 0l5.159 5.159m-1.5-1.5 1.409-1.409a2.25 2.25 0 0 1 3.182 0l2.909 2.909M3.75 4.5h16.5a1.5 1.5 0 0 1 1.5 1.5v12a1.5 1.5 0 0 1-1.5 1.5H3.75a1.5 1.5 0 0 1-1.5-1.5V6a1.5 1.5 0 0 1 1.5-1.5Z" />
                      </svg>
                    </div>
                  )}
                </button>

                {/* SKU + file (truncate, expandable) */}
                <div className="min-w-0 flex-1">
                  {!readOnly && editingId === a.id ? (
                    <div className="flex items-center gap-1">
                      <Input
                        value={editSku}
                        onChange={(e) => setEditSku(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && skuMutation.mutate({ assetId: a.id, sku: editSku })}
                        className="h-7"
                        autoFocus
                      />
                      <Button size="sm" variant="ghost" loading={skuMutation.isPending} onClick={() => skuMutation.mutate({ assetId: a.id, sku: editSku })}>Save</Button>
                      <Button size="sm" variant="ghost" onClick={() => setEditingId(null)}>×</Button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-1.5">
                      <span className="shrink-0 rounded bg-surface-tertiary px-1 text-[10px] font-semibold uppercase tracking-wide text-text-tertiary">SKU</span>
                      <button
                        type="button"
                        onClick={() => navigate(`/library/assets/${a.id}`)}
                        className="truncate text-sm font-medium text-brand-400 hover:underline"
                        title={a.sku}
                      >
                        {a.sku}
                      </button>
                      {!readOnly && (
                        <button
                          type="button"
                          className="shrink-0 text-xs text-text-tertiary hover:text-text-primary"
                          onClick={() => { setEditingId(a.id); setEditSku(a.sku); }}
                          aria-label="Edit SKU"
                        >✎</button>
                      )}
                    </div>
                  )}
                  <p className="mt-0.5 flex items-center gap-1.5 text-xs text-text-tertiary">
                    <span className="shrink-0 rounded bg-surface-tertiary px-1 text-[10px] font-semibold uppercase tracking-wide text-text-tertiary">File</span>
                    <span className="truncate" title={a.fileName}>{a.fileName}</span>
                  </p>
                  {/* Category (blue) + tags (orange) — inline editable while
                      the batch is open; static chips once COMPLETED. */}
                  <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                    {readOnly ? (
                      <>
                        <span className="rounded border border-blue-400/40 bg-blue-500/5 px-1.5 py-0.5 text-xs text-blue-600">
                          {a.categoryName ?? 'No category'}
                        </span>
                        {a.tags.map((t) => (
                          <span
                            key={t.id}
                            className="inline-flex items-center rounded-full bg-orange-500/15 px-2 py-0.5 text-xs font-medium text-orange-600"
                          >
                            {t.name}
                          </span>
                        ))}
                      </>
                    ) : (
                      <>
                        <div className="w-44">
                          <Combobox
                            size="sm"
                            options={categoryOptions}
                            value={a.categoryId}
                            onChange={(v) => rowPatch(a.id, { categoryId: v })}
                            placeholder="No category"
                            searchPlaceholder="Search categories…"
                            disabled={rowPatchPending(a.id)}
                          />
                        </div>
                        {a.tags.map((t) => (
                          <span
                            key={t.id}
                            className="inline-flex items-center gap-1 rounded-full bg-orange-500/15 px-2 py-0.5 text-xs font-medium text-orange-600"
                          >
                            {t.name}
                            <button
                              type="button"
                              className="text-orange-600/60 hover:text-orange-700"
                              onClick={() => rowPatch(a.id, { tagIds: a.tags.filter((x) => x.id !== t.id).map((x) => x.id) })}
                              aria-label={`Remove tag ${t.name}`}
                            >×</button>
                          </span>
                        ))}
                        <div className="w-36">
                          {/* Action picker: value stays null so each pick
                              appends a tag; inline create supported. */}
                          <Combobox
                            size="sm"
                            options={tagOptions.filter((t) => !a.tags.some((x) => x.id === t.value))}
                            value={null}
                            onChange={(v) => v && rowPatch(a.id, { tagIds: [...a.tags.map((x) => x.id), v] })}
                            placeholder="+ tag"
                            searchPlaceholder="Search or create…"
                            onCreate={onCreateTag}
                            disabled={rowPatchPending(a.id)}
                            allowClear={false}
                          />
                        </div>
                      </>
                    )}
                  </div>
                  {/* Linked variant (auto-linked at init or manually) — one-click Unlink */}
                  {a.primaryAssetId && (
                    <div className="mt-1.5 flex items-center gap-2">
                      <span className="rounded border border-purple-400/40 bg-purple-500/10 px-1.5 py-0.5 text-xs text-purple-500">
                        Variant of “{a.primarySku ?? a.primaryAssetId}”
                      </span>
                      {!readOnly && (
                        <Button
                          size="sm"
                          variant="ghost"
                          loading={
                            unlinkVariantMutation.isPending &&
                            unlinkVariantMutation.variables === a.id
                          }
                          onClick={() => unlinkVariantMutation.mutate(a.id)}
                        >
                          Unlink
                        </Button>
                      )}
                    </div>
                  )}
                  {/* Variant suggestion — leftover cases auto-link skipped; approve to link */}
                  {!readOnly && !a.primaryAssetId && a.variantSuggestion && (
                    <div className="mt-1.5 flex items-center gap-2">
                      <span className="rounded border border-purple-400/40 bg-purple-500/10 px-1.5 py-0.5 text-xs text-purple-500">
                        Variant of “{a.variantSuggestion.primarySku}” ({a.variantSuggestion.label})?
                      </span>
                      <Button
                        size="sm"
                        variant="ghost"
                        loading={
                          linkVariantMutation.isPending &&
                          linkVariantMutation.variables?.assetId === a.id
                        }
                        onClick={() =>
                          linkVariantMutation.mutate({
                            assetId: a.id,
                            primaryAssetId: a.variantSuggestion!.primaryAssetId,
                            label: a.variantSuggestion!.label,
                          })
                        }
                      >
                        Link
                      </Button>
                    </div>
                  )}
                  {a.failureReason && <p className="mt-1 text-xs text-red-400">{a.failureReason}</p>}
                </div>

                {/* Status + actions (inline) */}
                <div className="flex shrink-0 items-center gap-2">
                  <Badge variant={assetVariant(a.status)} size="sm">{a.status}</Badge>
                  {!readOnly && a.status === 'FAILED' && (
                    <Button
                      size="sm"
                      variant="ghost"
                      loading={retryMutation.isPending && retryMutation.variables === a.id}
                      onClick={() => retryMutation.mutate(a.id)}
                    >
                      Retry
                    </Button>
                  )}
                  {/* No discard while the worker still owns the row — an
                      in-flight job and an archive would race each other. */}
                  {!readOnly && a.status !== 'PROCESSING' && (
                    <Button
                      size="sm"
                      variant="ghost"
                      className="!text-red-400"
                      loading={discardingId === a.id}
                      onClick={() => void onDiscard(a)}
                    >
                      {a.status === 'FAILED' ? 'Delete' : 'Discard'}
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
          {visibleAssets.length === 0 && (
            <p className="py-8 text-center text-sm text-text-tertiary">
              {assets.length === 0 ? 'No assets in this batch.' : 'No assets match this status.'}
            </p>
          )}
        </div>
      </Card>

      {/* Bulk action bar (shared with the Assets page) */}
      {!readOnly && (
        <LibraryBulkBar
          selectedIds={[...selected]}
          categories={categoriesQuery.data ?? []}
          tags={tagsQuery.data ?? []}
          hasFailedSelected={hasFailedSelected}
          onDone={() => setSelected(new Set())}
        />
      )}
    </div>
  );
}
